/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";
import Immutable from "immutable";
import moment from "moment";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as KpiCalculator from "js/common/kpi-calculator";
import * as DashboardUtils from "js/dashboards/utils";
import {renderUserOrGroupDetails} from "js/dashboards/utils";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as Users from "js/common/users";
import * as Groups from "js/common/groups";
import * as Numbers from "js/common/utils/numbers";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";
import KpiPicker from "js/common/views/kpis/kpi-picker";
import Checkbox from "js/common/views/inputs/checkbox";
import * as Overlays from "js/dashboards/components/overlays";
import * as Formatter from "js/common/utils/formatter";
import {RadioGroup as RadioButtonGroup} from "@mui/material";
import RadioButton from "js/common/views/inputs/radio-button";
import LeaderboardRow from "js/dashboards/components/leaderboard/leaderboard-row";
import TopPerformerPanel from "js/dashboards/components/leaderboard/top-performer-panel";
import * as TopPerformers from "js/dashboards/components/leaderboard/top-performers";
import TopPerformersEditor from "js/dashboards/components/leaderboard/top-performers-editor";
import CubeTextField from "js/common/views/inputs/text-field";
import {PresentationClockContext} from "js/dashboards/hooks/use-presentation-timer";
import {DEFAULT_GAMIFIED_ZOOM, DEFAULT_ZOOM} from "js/dashboards/settings/appearance-utils";
import {betterMemo} from "js/common/utils/more-memo";
import * as Rata from "js/common/utils/remote-data";
import {useForceRender} from "js/common/hooks/use-force-render";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import KpiDetailsDialog from "js/oneview/kpi-details/dialog";


const MIN_WIDTH = 16;

const getPercentageOfTargetComplete = (value, target) => {
  if (!target || target === 0) {
    return 0;
  }
  return value / target;
};

const rankByValue = (data) => {
  return data.sortBy(
      item => -item.getIn(["kpi", "total", "value"])
  ).map((item, index) => item.set("rank", index + 1));
};

const rankByPercent = (data) => {
  return data.sortBy(item => {
    const target = item.getIn(["kpi", "target", "value"]);
    const value = item.getIn(["kpi", "total", "value"]);
    if (!target || target === 0) {
      return Number.MAX_SAFE_INTEGER; // Ensures these sort last
    }
    return -(value / target);
  }).map((item, index) => item.set("rank", index + 1));
};

const mapToTableRow = (data, kpiId) => {
  const rank = data.get("rank");
  const userId = data.get("userId");
  const kpi = data.get("kpi");
  const kpiConfig = KpiRepo.get(kpiId);
  const valueFormat = kpiConfig.get("valueFormat");
  const target = kpi.get("target");
  const total = kpi.get("total");
  const totalValueDisplay = Formatter.format(total.toJS(), {valueFormat, maxDisplayLength: 3});
  const hasTarget = target && target.get("value") > 0;
  const targetValueDisplay = hasTarget ? Formatter.format(target.toJS(), {valueFormat, maxDisplayLength: 3}) : "";
  const percentOfTarget = hasTarget ? getPercentageOfTargetComplete(total.get("value"), target.get("value")) : "";
  return Immutable.fromJS({
    rank,
    userId,
    totalValueDisplay,
    targetValueDisplay,
    percentOfTarget
  });
};

const rankData = (data, sortField, sortMode, filterZero, kpiId) => {
  const sortFn = sortField === "VALUE" ? rankByValue : rankByPercent;
  const sortedData = sortFn(data)
      .filter(row => filterZero ? row.getIn(["kpi", "total", "value"]) !== 0 && (sortField
          === "VALUE"
          || row.getIn(["kpi", "target", "value"])) !== 0 : true)
      .map(row => mapToTableRow(row, kpiId));

  if (sortMode === "DESC") {
    return sortedData;
  }

  return sortedData.reverse();
};

// Scrolls only the list, never the stage. The target depends on the row alone, not on the
// current scrollTop, which is mid-animation while a previous smooth scroll is still running.
// Below this list width (in CSS pixels, so it shrinks as the zoom grows) the badge, photo and value
// leave too little room to read a name, so rows drop the photo.
const COMPACT_ROW_WIDTH = 250;
// The panel tile pads its content by this much to make room for the title, and it is included in the
// measured height.
const TITLE_BAR_HEIGHT = 34;
const PANEL_SHARE = 0.46;
const LIST_HORIZONTAL_PADDING = 30;

const scrollRowIntoView = (list, index) => {
  const row = list && list.querySelectorAll("[data-test-id='leaderboard-row']")[index];
  if (!row || !list.scrollTo) {
    return;
  }
  list.scrollTo({top: Math.max(row.offsetTop - row.offsetHeight, 0), behavior: "smooth"});
};

const getRequestOptions = config => ({
  dateFromUI: moment().format("YYYY-MM-DD"),
  timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
  clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()).toArray(),
  matchAnyTagIds: config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List()).toArray(),
  matchAllTagIds: config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List()).toArray(),
  excludedTagIds: config.getIn(["tagFilter", "excludedTagIds"], Immutable.List()).toArray(),
  entityToIds: config.get("entityToIds", Immutable.Map()).toObject(),
  ...DashboardUtils.configToQualifierAjaxParams(config),
  alwaysAllowClientFilter: true,
  qualifierType: config.getIn(["qualifier", "type"], "GROUP"),
  qualifierId: config.getIn(["qualifier", "id"]) ?? Groups.getRootGroup().get("id")
});

const hiddenScrollbarStyle = css`
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Leaderboard = betterMemo({displayName: "Leaderboard"}, ({
  componentId,
  componentLoadId,
  dimensions,
  data,
  config,
  onRequestDialog,
  onParentConfigChange,
  onLoadComplete,
  isGamified = false,
  panelColor = null
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const {isAutoAdvancing: isPresenting, elapsedSeconds, requestDuration} = React.useContext(PresentationClockContext);
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [heldSeconds, setHeldSeconds] = React.useState(0);
  const forceRender = useForceRender();
  const listRef = React.useRef(null);
  const kpiId = config.get("kpiId");
  const filterZero = config.get("filterZero", false);
  const sortField = config.get("sortField");
  const sortMode = config.get("sortMode");
  const loaded = data && data.get("value");
  const leaderboardData = loaded ? loaded.get("leaderboard") : null;
  const showTopPerformers = TopPerformers.showsPanel(config);

  // The component remounts for every load, so the metrics for this load are fetched once and the
  // config from that load is the one that counts.
  const metricLoads = React.useMemo(
      () => kpiId && showTopPerformers
          ? TopPerformers.getMetricLoads(componentId, componentLoadId, config, getRequestOptions(config))
          : [],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [componentId, componentLoadId, kpiId, showTopPerformers]);
  const metricsLoading = metricLoads.some(load => !load.settled);

  React.useEffect(() => {
    let isMounted = true;
    metricLoads
        .filter(load => !load.settled)
        .forEach(load => load.promise.then(() => isMounted && forceRender()));
    return () => {
      isMounted = false;
    };
  }, [metricLoads, forceRender]);

  const isDataSettled = !!data && !Rata.isLoading(data) && !Rata.isInitial(data);
  React.useEffect(() => {
    if (onLoadComplete && isDataSettled && !metricsLoading) {
      onLoadComplete(DashboardUtils.dataId(componentLoadId, componentId));
    }
  }, [onLoadComplete, isDataSettled, metricsLoading, componentLoadId, componentId]);

  const rankedData = React.useMemo(
      () => {
        if (!kpiId || !leaderboardData) {
          return Immutable.List();
        }
        const ranked = rankData(leaderboardData, sortField, sortMode, filterZero, kpiId);
        return ranked.take(TopPerformers.getLeadersToShow(config, ranked.size));
      },
      [leaderboardData, sortField, sortMode, filterZero, kpiId, config]);

  const secondsPerLeader = TopPerformers.getSecondsPerLeader(config);
  const cycleLength = TopPerformers.getCycleLength(config, rankedData.size);
  const isAutoAdvancing = isPresenting && TopPerformers.isAutoAdvanceEnabled(config) && cycleLength > 0;

  // While the side panel's metrics are still loading the highlight stays on its leader past the end
  // of their slot, and every second held pushes the rest of the cycle, and the slide, back.
  const isHolding = isAutoAdvancing && metricsLoading && heldSeconds < TopPerformers.MAX_METRIC_WAIT_SECONDS;
  const secondsIntoCycle = Math.max(elapsedSeconds - heldSeconds, 0);
  const isAtEndOfSlot = secondsIntoCycle > 0 && secondsIntoCycle % secondsPerLeader === 0;
  const shouldHold = isHolding && isAtEndOfSlot;

  const secondsShown = shouldHold ? secondsIntoCycle - 1 : secondsIntoCycle;
  const didHoldRef = React.useRef(false);
  React.useEffect(() => {
    if (elapsedSeconds === 0) {
      didHoldRef.current = false;
      setHeldSeconds(0);
    } else if (shouldHold) {
      didHoldRef.current = true;
      setHeldSeconds(held => held + 1);
    } else if (didHoldRef.current && !metricsLoading) {
      // Restart the held leader's slot so the numbers they were waiting on get their full time.
      didHoldRef.current = false;
      setHeldSeconds(elapsedSeconds - Math.floor(secondsShown / secondsPerLeader) * secondsPerLeader);
    }
  }, [elapsedSeconds, shouldHold, metricsLoading, secondsShown, secondsPerLeader]);

  React.useEffect(() => {
    if (!isAutoAdvancing) {
      return;
    }
    // The extra second stops the slide ending on the same tick the highlight is held.
    requestDuration(componentId, cycleLength * secondsPerLeader + heldSeconds + (isHolding ? 1 : 0));
    return () => requestDuration(componentId, 0);
  }, [isAutoAdvancing, requestDuration, componentId, cycleLength, secondsPerLeader, heldSeconds, isHolding]);

  let highlightedIndex = 0;
  if (isAutoAdvancing) {
    highlightedIndex = TopPerformers.getAutoAdvanceIndex(secondsShown, secondsPerLeader, cycleLength);
  } else if (showTopPerformers && hoveredIndex != null && hoveredIndex < rankedData.size) {
    highlightedIndex = hoveredIndex;
  }

  React.useEffect(() => {
    if (isAutoAdvancing) {
      scrollRowIntoView(listRef.current, highlightedIndex);
    }
  }, [isAutoAdvancing, highlightedIndex]);

  const handleRowClick = React.useCallback(
      userId => renderDialog(config, onParentConfigChange, onRequestDialog, userId),
      [config, onParentConfigChange, onRequestDialog]);
  const handleRowHover = showTopPerformers && !isAutoAdvancing ? setHoveredIndex : null;
  const handleListLeave = React.useCallback(() => setHoveredIndex(null), []);

  if (!kpiId) {
    return <Overlays.Message coverOtherContent={false}>
      Choose a metric to display the leaderboard
    </Overlays.Message>;
  }

  if (!leaderboardData || leaderboardData.size === 0) {
    return <Overlays.Message coverOtherContent={false}>
      No data available for this metric
    </Overlays.Message>;
  }

  const height = dimensions.height - TITLE_BAR_HEIGHT;
  const listWidth = (dimensions.width || 0) * (showTopPerformers ? 1 - PANEL_SHARE : 1) - LIST_HORIZONTAL_PADDING;
  const isCompact = listWidth > 0 && listWidth < COMPACT_ROW_WIDTH;
  const highlightedRow = rankedData.get(highlightedIndex);

  return (
      <div
          style={{
            height,
            display: "flex",
            backgroundColor: panelColor || theme.palette.background.card,
            padding: "10px 0"
          }}>
        <div
            ref={listRef}
            data-test-id="leaderboard"
            onMouseLeave={handleListLeave}
            css={isPresenting && hiddenScrollbarStyle}
            style={{
              position: "relative",
              flex: 1,
              minWidth: 0,
              overflow: "auto",
              padding: "0 15px"
            }}>
          {rankedData.isEmpty() && <div
              style={{
                display: "flex",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                fontSize: 13
              }}>No data to display</div>}
          {rankedData.map((rowData, index) => {
            const percentOfTarget = rowData.get("percentOfTarget");
            return (
                <LeaderboardRow
                    key={rowData.get("userId")}
                    index={index}
                    rank={index + 1}
                    user={Users.getUser(rowData.get("userId"))}
                    value={rowData.get("totalValueDisplay")}
                    target={rowData.get("targetValueDisplay")}
                    percentOfTarget={percentOfTarget ? Numbers.toPercentageStr(percentOfTarget) : "--"}
                    isHighlighted={index === highlightedIndex}
                    onClick={handleRowClick}
                    onHover={handleRowHover}
                    sortField={sortField}
                    isCompact={isCompact}
                />
            );
          })}
        </div>
        {showTopPerformers && highlightedRow && (
            <div style={{width: `${PANEL_SHARE * 100}%`, flexShrink: 0}}>
              <TopPerformerPanel
                  config={config}
                  rank={highlightedIndex + 1}
                  user={Users.getUser(highlightedRow.get("userId"))}
                  value={getDisplayValue(highlightedRow, sortField)}
                  metricValues={metricLoads.map(load => (load.settled ? load.values : undefined))}
                  height={height - 20}
                  width={dimensions.width}
                  // Gamified dashboards open at a higher zoom, and that default is what the panel is
                  // designed to look like, so zoom is measured from it.
                  zoom={(dimensions.zoom || 1) / (isGamified ? DEFAULT_GAMIFIED_ZOOM / DEFAULT_ZOOM : 1)} />
            </div>
        )}
      </div>
  );
});

const getDisplayValue = (rowData, sortField) => {
  if (sortField === "VALUE") {
    return rowData.get("totalValueDisplay");
  }
  const percentOfTarget = rowData.get("percentOfTarget");
  return percentOfTarget ? Numbers.toPercentageStr(percentOfTarget) : "--";
};

// Editor component
const LeaderboardEditor = React.memo(({
  component,
  onComponentChange
}) => {
  const config = component.get("dataConfig");
  const {theme} = React.useContext(CustomThemeContext);

  const onConfigChange = React.useCallback(newDataConfig => {
    const showPanel = TopPerformers.showsPanel(newDataConfig);
    const panelToggled = showPanel !== TopPerformers.showsPanel(component.get("dataConfig"));
    const updated = component.set("dataConfig", newDataConfig);
    onComponentChange(panelToggled && updated.has("layout")
        ? updated.update("layout", layout => TopPerformers.resizeLayoutForPanel(
            layout, showPanel, {columns: DashboardUtils.dashboardColumns, minWidth: MIN_WIDTH}))
        : updated);
  }, [onComponentChange, component]);

  const kpis = React.useMemo(
      () => Immutable.fromJS(KpiRepo
          .getAll().toJSON())
          .filter(k => k.get("visible") || k.get("id") === config.get("kpiId")),
      [config]
  );

  const optionHeadingStyle = {
    fontSize: 14,
    fontWeight: "bold",
    margin: 0,
    marginTop: 30,
    padding: 0
  };

  const optionDescriptionStyle = {
    fontSize: 13,
    margin: 0,
    padding: 0,
    opacity: 0.7,
    lineHeight: 1.3
  };

  const formControlWrapperStyle = {
    background: theme.palette.background.card,
    border: `1px solid ${theme.palette.border.lightest}`,
    padding: "2px 5px",
    marginTop: 5,
    borderRadius: 5
  };

  return (
      <div style={{padding: "0.5rem"}}>
        <DataConfigToolbar config={config} onConfigChange={onConfigChange} />

        <div style={{width: "50%", marginBottom: "0.3rem", marginTop: "2rem", marginLeft: "1rem"}}>
          <p style={{...optionHeadingStyle, marginTop: 20}}>What metric would you like to use for your leaderboard?</p>
          <KpiPicker
              placeholder="Choose a metric for your leaderboard"
              multiSelect={false}
              clearable={false}
              kpis={kpis}
              selectedKpiId={config.get("kpiId")}
              onChange={kpiId => {
                const kpi = KpiRepo.get(kpiId);
                onConfigChange(
                    config
                        .set("kpiId", kpiId)
                        .set("title", kpi.get("name"))
                );
              }}
          />


          <p style={optionHeadingStyle}>Filter out zero values?</p>
          <p style={optionDescriptionStyle}>If you select this option we wont include users with 0 as their value. This
            can be useful if you have a large amount of users and want to avoid clutter.</p>
          <div style={formControlWrapperStyle}>
            <Checkbox
                style={{marginTop: 0}}
                label="Filter out users who have a value of 0"
                checked={config.get("filterZero", false)}
                onCheck={(e, isChecked) => {
                  onConfigChange(config.set("filterZero", isChecked));
                }}
            />
          </div>


          <p style={optionHeadingStyle}>What would you like to rank by?</p>
          <p style={optionDescriptionStyle}>For most cases ranking by value is recommended. However there are some
            cases, like if you are comparing senior recruiters to junior recruiters where you may to choose to rank by %
            of their target achieved.</p>
          <RadioButtonGroup
              style={formControlWrapperStyle}
              name="sortField"
              value={config.get("sortField")}
              onChange={e => {
                onConfigChange(config.set("sortField", e.target.value));
              }}>
            <div style={{display: "flex"}}>
              <RadioButton
                  value="VALUE"
                  label="Value" />
              <RadioButton
                  value="PERCENTAGE"
                  label="Percentage of users' target" />
            </div>
          </RadioButtonGroup>


          <p style={optionHeadingStyle}>What order would you like to show the leaderboard?</p>
          <p style={optionDescriptionStyle}>Descending is recommended for most metrics, ranking highest values first
            (1st, 2nd, 3rd...). Choose ascending if a lower number is actually the better result.</p>
          <RadioButtonGroup
              style={formControlWrapperStyle}
              name="sortMode"
              value={config.get("sortMode")}
              onChange={e => {
                onConfigChange(config.set("sortMode", e.target.value));
              }}>
            <div style={{display: "flex"}}>
              <RadioButton
                  value="DESC"
                  label="Descending" />
              <RadioButton
                  value="ASC"
                  label="Ascending" />
            </div>
          </RadioButtonGroup>


          <p style={optionHeadingStyle}>How many people should the leaderboard show?</p>
          <div style={{...formControlWrapperStyle, padding: "5px 10px"}}>
            <CubeTextField
                label="Leaders to show"
                type="number"
                helperText="Leave blank to show everyone"
                testId="leaderboard-leaders-to-show"
                value={config.get("leadersToShow") ?? ""}
                onKeyDown={blockNonIntegerKeys}
                inputProps={{min: 1, step: 1}}
                onChange={e => onConfigChange(config.set("leadersToShow", toWholeNumberOrNull(e.target.value)))} />
          </div>


          <p style={optionHeadingStyle}>How should the leaderboard present?</p>
          <p style={optionDescriptionStyle}>With auto-advance on, a presenting dashboard highlights each leader in
            turn, and the tab stays up until every leader has been shown, even past its minimum slide time.</p>
          <div style={formControlWrapperStyle}>
            <Checkbox
                style={{marginTop: 0}}
                label="Auto-advance through leaders when presenting"
                testId="leaderboard-auto-advance"
                checked={TopPerformers.isAutoAdvanceEnabled(config)}
                onCheck={(e, isChecked) => onConfigChange(TopPerformers.setAutoAdvance(config, isChecked))} />
          </div>
          {TopPerformers.isAutoAdvanceEnabled(config) && (
            <div style={{...formControlWrapperStyle, display: "flex", gap: "1rem", padding: "5px 10px"}}>
              <CubeTextField
                  label="Seconds Per Leader"
                  type="number"
                  testId="leaderboard-seconds-per-leader"
                  placeholder={String(TopPerformers.DEFAULT_SECONDS_PER_LEADER)}
                  value={config.has("secondsPerLeader")
                      ? config.get("secondsPerLeader") ?? ""
                      : TopPerformers.DEFAULT_SECONDS_PER_LEADER}
                  onKeyDown={blockNonIntegerKeys}
                  inputProps={{min: 1, step: 1}}
                  onChange={e => onConfigChange(config.set("secondsPerLeader", toWholeNumberOrNull(e.target.value)))} />
              <CubeTextField
                  label="Leaders to cycle through"
                  type="number"
                  helperText="Leave blank to cycle through every leader"
                  testId="leaderboard-leaders-to-cycle"
                  value={config.get("leadersToCycle") ?? ""}
                  onKeyDown={blockNonIntegerKeys}
                  inputProps={{min: 1, step: 1}}
                  onChange={e => onConfigChange(config.set("leadersToCycle", toWholeNumberOrNull(e.target.value)))} />
            </div>
          )}
        </div>

        <div style={{margin: "0 1rem 1rem"}}>
          <p style={optionHeadingStyle}>Show extra metrics for the highlighted leader?</p>
          <p style={optionDescriptionStyle}>Displays the highlighted leader's profile picture and up to 3 metrics
            beside the leaderboard. Hover over a leader to see their details. Each metric uses its own timeframe
            and tags in place of the leaderboard's, but keeps the leaderboard's group and client filter.</p>
          <TopPerformersEditor config={config} onConfigChange={onConfigChange} />
        </div>
      </div>
  );
});

// type="number" otherwise accepts "e", "+", "-" and "."
const blockNonIntegerKeys = e => {
  if (["e", "E", "+", "-", "."].includes(e.key)) {
    e.preventDefault();
  }
};

const toWholeNumberOrNull = value => {
  const number = parseInt(value, 10);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const renderDialog = (config, onParentConfigChange, onRequestDialog, userId) => {
  const kpi = KpiRepo.get(config.get("kpiId"));

  const kpiDetailsOptions = {
    kpiId: config.get("kpiId"),
    timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
    clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()),
    entityToIds: config.get("entityToIds", Immutable.Map()).toObject(),
    ...DashboardUtils.configToQualifierAjaxParams(config)
  };

  const dialogConfig = config.set("qualifier", Immutable.Map({
    type: "USER",
    id: userId
  }));

  const title = (
      <div style={{display: "inline-block"}}>
      <span
          style={{
            fontSize: 20,
            textTransform: "none",
            fontWeight: 500
          }}>
          {kpi.get("name")}
      </span>
        <span style={{fontWeight: 500}}>
        {` (${TimeframeRepo.parse(config.get("timeframe").toJS()).attributes.name})`}
      </span>
        {renderUserOrGroupDetails(dialogConfig)}
      </div>
  );
  const kpiDialogRef = React.createRef();
  const handleKpiDialogRequestClose = () => closeDialog();
  const handleDialogRequestClose = () => kpiDialogRef.current.handleCloseRequest();
  const closeDialog = onRequestDialog(
      <KpiDetailsDialog
          ref={kpiDialogRef}
          userId={userId}
          renderContentOnly
          {...kpiDetailsOptions}
          matchAnyTagIds={config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List())}
          matchAllTagIds={config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List())}
          excludedTagIds={config.getIn(["tagFilter", "excludedTagIds"], Immutable.List())}
          onRequestClose={handleKpiDialogRequestClose}
          onGroupClick={groupId => {
            onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
              type: "GROUP",
              id: groupId
            })), true);
            closeDialog();
          }}
          onUserClick={userId => {
            onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
              type: "USER",
              id: userId
            })), true);
            closeDialog();
          }} />,
      {title: title, onRequestClose: handleDialogRequestClose});
};

export default Immutable.fromJS({
  type: "Leaderboard",
  label: "Leaderboard",
  titleIsEditable: true,
  getTitle: component => {
    const config = component.get("dataConfig");
    const kpiId = config.get("kpiId");

    if (kpiId) {
      if (component.get("title") || component.get("title") === "") {
        return component.get("title");
      } else {
        return `${KpiRepo.get(kpiId).get("name")} Leaderboard`;
      }
    } else {
      return "New Leaderboard";
    }
  },
  getDefaultData: () => Immutable.Map(),
  getReactComponent: () => Leaderboard,
  getEditorReactComponent: () => LeaderboardEditor,
  canFullScreen: true,
  layout: {
    min: {width: MIN_WIDTH, height: 8},
    max: {width: 48, height: 48},
    // Scaled up by the zoom when added, so at the gamified default it must still fit a 16:9 stage.
    init: {width: 16, height: 14}
  },
  applyParentConfig: (parentConfig, dataConfig) => DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig),
  load: component => {
    const config = component.get("dataConfig");
    const kpiId = config.get("kpiId");

    if (!kpiId) {
      return Promise.resolve(Immutable.Map({leaderboard: Immutable.List()}));
    }

    return KpiCalculator
        .targetedLeaderboard(kpiId, getRequestOptions(config))
        .then(leaderboard => Immutable.Map({leaderboard: Immutable.fromJS(leaderboard)}));
  },
  // The top performer metrics load inside the component so the leaderboard can show before them.
  requiresFurtherLoad: true
});